import { PinIcon, ImageIcon } from '../../icons/icons';
import { serverUrl } from '../../../config';

require('./index.css').toString();

export default class DynamicMap {

  static get toolbox() {
    return {
      icon: PinIcon,
      title: 'Dynamic Map',
    };
  }

  static get isReadOnlySupported() {
    return true;
  }

  constructor({ data, config, api, readOnly, block }) {
    this.api = api;
    this.readOnly = readOnly;
    this.block = block;
    this.config = config;

    this._data = {
      title: data.title || config?.title || '',
      zoom: data.zoom ?? config?.defaultZoom ?? 3,
      centerLat: data.centerLat ?? config?.defaultCenterLat ?? 24.201,
      centerLng: data.centerLng ?? config?.defaultCenterLng ?? 63.72,
      mapStyle: data.mapStyle || config?.mapStyle || 'light',
      region: data.region || '',
      country: data.country || '',
      city: data.city || '',
      url: data.url || '',
      mid: data.mid || '',
      x: data.x || '',
      y: data.y || '',
      width: data.width || '',
      height: data.height || '',
      maxWidth: data.maxWidth || '',
      maxHeight: data.maxHeight || '',
    };

    this._CSS = {
      dmModalApplyBtn: "dm-apply-btn",
      dmModalCancelBtn: "dm-cancel-btn",
      dmModalResetContainer: "dm-reset-container",
      dmModalResetBtn: "dm-reset-btn",
      dmOnscreenResetBtn: "dm-onscreen-reset-btn",
      mapStatus: "dynamic-map-status",
      mapRender: "dynamic-map-render",
      mapContainer: "dynamic-map-container",
      mapModalBtn: "dynamic-map-modal-show-btn",
      // standard css
      blockBtn: "block-btn",
      blockBtnSmall: "small",
      blockBtnLarge: "large",
      blockBtnLargeWide: "wide",
      largeIcon: "large-icon",
      xlIcon: "xl-icon",
      xxlIcon: "xxl-icon",
      save: "save",
      clear: "clear",
      cancel: "cancel",
      focusVisibleBtn: "focus-visible-btn",
      tuneWrapper: "editor-block-tune",
      tuneTitle: "editor-block-tune-title",
      tuneText: "editor-block-tune-text",
      tuneInput: "editor-block-tune-input",
      tuneButton: "editor-block-tune-button",
      editorFont: "editor-font",
      editorBlockToolbar: "editor-block-toolbar",
      editorBlockModalBackdrop: "editor-block-modal-backdrop",
      editorBlockModal: "editor-block-modal",
      editorBlockModalTitle: "editor-block-modal-title",
      editorBlockModalLabel: "editor-block-modal-label",
      editorBlockModalText: "editor-block-modal-text",
      editorBlockModalInput: "editor-block-modal-input",
      adminFont: "admin-font",
    }
    
    this._allHotels = [];
    this._iframe = null;
    this._savedMapState = null;
    this._brandLogos = config?.brandLogos || {};
    this._slideId = config?.slideId || '';

    // Bind the message handler so we can add/remove it
    this._onMessage = this._handleMessage.bind(this);
  }

  render() {
    const css = this._CSS;
    this._container = document.createElement('div');
    this._container.classList.add(css.mapContainer);
    
    this._onScreenResetBtn = document.createElement('button');
    this._onScreenResetBtn.classList.add(css.dmOnscreenResetBtn, css.blockBtn, css.blockBtnLarge, css.blockBtnLargeWide, css.clear);
    this._onScreenResetBtn.textContent = 'Reset configuration';
    this._onScreenResetBtn.style.display = 'none';
    this._onScreenResetBtn.addEventListener('click', () => {
      this._onReset();
    });

    // hidden input to prevent empty block creation
    const hiddenInput = document.createElement('input');
    hiddenInput.setAttribute('style', 'visibility:hidden;width:0;height:0;padding:0;margin:0;border:0;position:absolute');
    this._container.appendChild(hiddenInput);

    // Map wrapper (iframe goes here)
    this._mapWrapper = document.createElement('div');
    this._mapWrapper.classList.add(css.mapRender);
    this._container.appendChild(this._mapWrapper);

    // Status indicator
    this._status = document.createElement('div');
    this._status.classList.add(css.mapStatus);
    this._container.appendChild(this._status);

    // Settings button (edit mode only)
    if (!this.readOnly) {
      this._settingsBtn = document.createElement('button');
      this._settingsBtn.classList.add(css.blockBtn, css.blockBtnLarge, css.blockBtnLargeWide, css.mapModalBtn);
      this._settingsBtn.innerText = 'Configure';
      this._settingsBtn.addEventListener('click', () => this._showModal());
      this._container.appendChild(this._onScreenResetBtn);
      this._container.appendChild(this._settingsBtn);

      this._modal = this._buildModal();
      this._container.appendChild(this._modal);
      this._updateResetButton();
    }

    // Listen for postMessage from the iframe
    window.addEventListener('message', this._onMessage);

    // Render the iframe
    this._renderIframe();

    return this._container;
  }

  _buildIframeSrc() {
    const params = new URLSearchParams({
      serverUrl: serverUrl,
      lat: this._data.centerLat,
      lng: this._data.centerLng,
      zoom: this._data.zoom,
      mapStyle: this._data.mapStyle,
      title: this._data.title,
      slideId: this._slideId,
    });
    return `/dynamic-map.html?${params.toString()}`;
  }

  _renderIframe() {
    this._mapWrapper.innerHTML = '';

    const iframe = document.createElement('iframe');
    iframe.src = this._buildIframeSrc();
    this._iframe = iframe;
    this._mapWrapper.appendChild(iframe);
  }

  _postToIframe(type, payload) {
    if (this._iframe && this._iframe.contentWindow) {
      this._iframe.contentWindow.postMessage({ type, payload }, '*');
    }
  }

  _sendBrandLogos() {
    if (!this._brandLogos || !Object.keys(this._brandLogos).length) return;
    this._postToIframe('dynamic-map-theme-data', { brandLogos: this._brandLogos });
  }

  _handleMessage(event) {
    if (!event.data || typeof event.data !== 'object') return;
    if (event.data.slideId !== this._slideId) return;

    switch (event.data.type) {
      case 'dynamic-map-ready':
        this._setStatus('Map ready');
        setTimeout(() => this._hideStatus(), 2000);
        this._sendBrandLogos();
        break;

      case 'dynamic-map-hotels-loaded':
        this._allHotels = event.data.payload.hotels || [];
        this._setStatus(`${event.data.payload.count} hotels loaded`);
        setTimeout(() => this._hideStatus(), 3000);

        // Save initial map state for reset
        this._savedMapState = {
          zoom: this._data.zoom,
          centerLat: this._data.centerLat,
          centerLng: this._data.centerLng,
        };

        // Populate filter dropdowns from hotel data
        this._populateFilters();

        // Apply any pre-existing filter
        if (this._data.region || this._data.country || this._data.city) {
          this._applyFilter();
        }
        break;

      case 'dynamic-map-zoom-changed':
        this._data.zoom = event.data.payload.zoom;
        this._data.centerLat = event.data.payload.centerLat;
        this._data.centerLng = event.data.payload.centerLng;
        if (this.block?.dispatchChange) {
          this.block.dispatchChange();
        }
        break;

      case 'dynamic-map-error':
        this._setStatus(event.data.payload.message || 'Map error');
        break;

      default:
        break;
    }
  }


  static REGION_MAP = {
    // Europe
    AL:'Europe',AD:'Europe',AT:'Europe',BY:'Europe',BE:'Europe',BA:'Europe',BG:'Europe',HR:'Europe',CY:'Europe',CZ:'Europe',
    DK:'Europe',EE:'Europe',FI:'Europe',FR:'Europe',DE:'Europe',GR:'Europe',HU:'Europe',IS:'Europe',IE:'Europe',IT:'Europe',
    XK:'Europe',LV:'Europe',LI:'Europe',LT:'Europe',LU:'Europe',MT:'Europe',MD:'Europe',MC:'Europe',ME:'Europe',NL:'Europe',
    MK:'Europe',NO:'Europe',PL:'Europe',PT:'Europe',RO:'Europe',RU:'Europe',SM:'Europe',RS:'Europe',SK:'Europe',SI:'Europe',
    ES:'Europe',SE:'Europe',CH:'Europe',UA:'Europe',GB:'Europe',VA:'Europe',GE:'Europe',AM:'Europe',AZ:'Europe',TR:'Europe',
    // Africa
    DZ:'Africa',AO:'Africa',BJ:'Africa',BW:'Africa',BF:'Africa',BI:'Africa',CV:'Africa',CM:'Africa',CF:'Africa',TD:'Africa',
    KM:'Africa',CD:'Africa',CG:'Africa',CI:'Africa',DJ:'Africa',EG:'Africa',GQ:'Africa',ER:'Africa',SZ:'Africa',ET:'Africa',
    GA:'Africa',GM:'Africa',GH:'Africa',GN:'Africa',GW:'Africa',KE:'Africa',LS:'Africa',LR:'Africa',LY:'Africa',MG:'Africa',
    MW:'Africa',ML:'Africa',MR:'Africa',MU:'Africa',MA:'Africa',MZ:'Africa',NA:'Africa',NE:'Africa',NG:'Africa',RW:'Africa',
    ST:'Africa',SN:'Africa',SC:'Africa',SL:'Africa',SO:'Africa',ZA:'Africa',SS:'Africa',SD:'Africa',TZ:'Africa',TG:'Africa',
    TN:'Africa',UG:'Africa',ZM:'Africa',ZW:'Africa',RE:'Africa',
    // Asia
    AF:'Asia',BH:'Asia',BD:'Asia',BT:'Asia',BN:'Asia',KH:'Asia',CN:'Asia',IN:'Asia',ID:'Asia',IR:'Asia',IQ:'Asia',IL:'Asia',
    JP:'Asia',JO:'Asia',KZ:'Asia',KW:'Asia',KG:'Asia',LA:'Asia',LB:'Asia',MY:'Asia',MV:'Asia',MN:'Asia',MM:'Asia',NP:'Asia',
    KP:'Asia',OM:'Asia',PK:'Asia',PS:'Asia',PH:'Asia',QA:'Asia',SA:'Asia',SG:'Asia',KR:'Asia',LK:'Asia',SY:'Asia',TW:'Asia',
    TJ:'Asia',TH:'Asia',TL:'Asia',TM:'Asia',AE:'Asia',UZ:'Asia',VN:'Asia',YE:'Asia',
    // Oceania
    AU:'Oceania',FJ:'Oceania',NZ:'Oceania',PG:'Oceania',WS:'Oceania',SB:'Oceania',TO:'Oceania',VU:'Oceania',
    // Americas
    AR:'Americas',BS:'Americas',BB:'Americas',BZ:'Americas',BO:'Americas',BR:'Americas',CA:'Americas',CL:'Americas',
    CO:'Americas',CR:'Americas',CU:'Americas',DO:'Americas',EC:'Americas',SV:'Americas',GT:'Americas',GY:'Americas',
    HT:'Americas',HN:'Americas',JM:'Americas',MX:'Americas',NI:'Americas',PA:'Americas',PY:'Americas',PE:'Americas',
    PR:'Americas',SR:'Americas',TT:'Americas',US:'Americas',UY:'Americas',VE:'Americas',AG:'Americas',DM:'Americas',
    GD:'Americas',KN:'Americas',LC:'Americas',VC:'Americas',AW:'Americas',CW:'Americas',
  };

  _getRegionForHotel(hotel) {
    const code = (hotel.countryCode || '').toUpperCase();
    return DynamicMap.REGION_MAP[code] || 'Other';
  }

  _populateFilters() {
    if (!this._regionSelect) return;

    const hotels = this._allHotels;
    const regions = new Set();
    hotels.forEach(h => {
      const region = this._getRegionForHotel(h);
      if (region) regions.add(region);
    });

    this._regionSelect.innerHTML = '<option value="">All Regions</option>';
    [...regions].sort().forEach(r => {
      const opt = document.createElement('option');
      opt.value = r;
      opt.textContent = r;
      this._regionSelect.appendChild(opt);
    });

    // Restore saved values and cascade
    if (this._data.region) {
      this._regionSelect.value = this._data.region;
      this._populateCountries();
      this._showRow(this._countryRow);

      if (this._data.country) {
        this._countrySelect.value = this._data.country;
        this._populateCities();
        this._showRow(this._cityRow);

        if (this._data.city) {
          this._citySelect.value = this._data.city;
        }
      }
    }
  }

  _getFilteredByRegion() {
    if (!this._data.region) return this._allHotels;
    return this._allHotels.filter(h => this._getRegionForHotel(h) === this._data.region);
  }

  _populateCountries() {
    const hotels = this._getFilteredByRegion();
    const countries = new Set();
    hotels.forEach(h => { if (h.country) countries.add(h.country); });

    this._countrySelect.innerHTML = '<option value="">All Countries</option>';
    [...countries].sort().forEach(c => {
      const opt = document.createElement('option');
      opt.value = c;
      opt.textContent = c;
      this._countrySelect.appendChild(opt);
    });
    this._countrySelect.disabled = false;
  }

  _populateCities() {
    let hotels = this._getFilteredByRegion();
    if (this._data.country) {
      hotels = hotels.filter(h => h.country === this._data.country);
    }

    const cities = new Set();
    hotels.forEach(h => { if (h.city) cities.add(h.city); });

    this._citySelect.innerHTML = '<option value="">All Cities</option>';
    [...cities].sort().forEach(c => {
      const opt = document.createElement('option');
      opt.value = c;
      opt.textContent = c;
      this._citySelect.appendChild(opt);
    });
    this._citySelect.disabled = false;
  }

  _onRegionChange() {
    // Save map state before first filter change
    if (!this._savedMapState) {
      this._savedMapState = {
        zoom: this._data.zoom,
        centerLat: this._data.centerLat,
        centerLng: this._data.centerLng,
      };
    }

    this._data.region = this._regionSelect.value;
    this._data.country = '';
    this._data.city = '';

    if (this._data.region) {
      this._populateCountries();
      this._showRow(this._countryRow);
    } else {
      this._hideRow(this._countryRow);
    }

    // Always hide city when region changes
    this._hideRow(this._cityRow);
    this._citySelect.innerHTML = '<option value="">All Cities</option>';
    this._citySelect.disabled = true;

    // Show/hide reset button
    this._updateResetButton();
    this._applyFilter();
  }

  _onCountryChange() {
    if (!this._savedMapState) {
      this._savedMapState = {
        zoom: this._data.zoom,
        centerLat: this._data.centerLat,
        centerLng: this._data.centerLng,
      };
    }

    this._data.country = this._countrySelect.value;
    this._data.city = '';

    if (this._data.country) {
      this._populateCities();
      this._showRow(this._cityRow);
    } else {
      this._hideRow(this._cityRow);
    }

    this._citySelect.innerHTML = '<option value="">All Cities</option>';
    if (this._data.country) this._populateCities();

    this._updateResetButton();
    this._applyFilter();
  }

  _onCityChange() {
    if (!this._savedMapState) {
      this._savedMapState = {
        zoom: this._data.zoom,
        centerLat: this._data.centerLat,
        centerLng: this._data.centerLng,
      };
    }

    this._data.city = this._citySelect.value;
    this._updateResetButton();
    this._applyFilter();
  }

  _applyFilter() {
    let filtered = this._allHotels;

    if (this._data.region) {
      filtered = filtered.filter(h => this._getRegionForHotel(h) === this._data.region);
    }
    if (this._data.country) {
      filtered = filtered.filter(h => h.country === this._data.country);
    }
    if (this._data.city) {
      filtered = filtered.filter(h => h.city === this._data.city);
    }

    // Send filtered list to iframe
    this._postToIframe('dynamic-map-filter', { hotels: filtered });

    // Auto-center on filtered hotels
    if (filtered.length > 0 && (this._data.region || this._data.country || this._data.city)) {
      this._autoCenterOnHotels(filtered);
    }

    this._dirty = true;
    this._showSaveButton();
  }

  _autoCenterOnHotels(hotels) {
    const validHotels = hotels.filter(h => h.latitude && h.longitude);
    if (validHotels.length === 0) return;

    let minLat = Infinity, maxLat = -Infinity;
    let minLng = Infinity, maxLng = -Infinity;

    validHotels.forEach(h => {
      const lat = parseFloat(h.latitude);
      const lng = parseFloat(h.longitude);
      if (lat < minLat) minLat = lat;
      if (lat > maxLat) maxLat = lat;
      if (lng < minLng) minLng = lng;
      if (lng > maxLng) maxLng = lng;
    });

    this._postToIframe('dynamic-map-set-view', {
      bounds: { minLat, maxLat, minLng, maxLng },
    });
  }

  _onReset() {
    // Reset hotel filter
    this._data.region = '';
    this._data.country = '';
    this._data.city = '';

    this._regionSelect.value = '';
    this._countrySelect.innerHTML = '<option value="">All Countries</option>';
    this._countrySelect.disabled = true;
    this._citySelect.innerHTML = '<option value="">All Cities</option>';
    this._citySelect.disabled = true;

    this._hideRow(this._countryRow);
    this._hideRow(this._cityRow);

    // Restore map position
    if (this._savedMapState) {
      this._postToIframe('dynamic-map-set-view', {
        center: {
          lat: this._savedMapState.centerLat,
          lng: this._savedMapState.centerLng,
        },
        zoom: this._savedMapState.zoom,
      });
    }

    // Show all hotels
    this._postToIframe('dynamic-map-filter', { hotels: this._allHotels });

    this._updateResetButton();
    this._dirty = true;
    this._showSaveButton();
  }

  _updateResetButton() {
    if (this._resetBtn) {
      if (this._data.region || this._data.country || this._data.city) {
        this._resetBtn.style.display = 'inline-block';
      } else {
        this._resetBtn.style.display = 'none';
      }
    }
    if (this._onScreenResetBtn) {
      if (this._data.region || this._data.country || this._data.city) {
        this._onScreenResetBtn.style.display = 'inline-block';
      } else {
        this._onScreenResetBtn.style.display = 'none';
      }
    }
  }

  _showRow(row) {
    if (row) row.style.display = '';
  }

  _hideRow(row) {
    if (row) row.style.display = 'none';
  }


  _setStatus(msg) {
    if (this._status) {
      this._status.textContent = msg;
      this._status.classList.add('visible');
    }
  }

  _hideStatus() {
    if (this._status) {
      this._status.classList.remove('visible');
    }
  }

  _buildModal() {
    const css = this._CSS;
    const modal = document.createElement('div');
    modal.classList.add('dynamic-map-modal');

    const backdrop = document.createElement('div');
    // backdrop.classList.add('dynamic-map-modal-backdrop');
    backdrop.classList.add(css.editorBlockModalBackdrop);
    backdrop.addEventListener('click', () => this._hideModal());

    const inner = document.createElement('div');
    inner.classList.add('dynamic-map-modal-inner', css.editorBlockModal);

    const heading = document.createElement('h4');
    heading.classList.add(css.editorBlockModalTitle);
    heading.textContent = 'Map settings / hotel filter';

    // Title
    const titleLabel = document.createElement('label');
    titleLabel.classList.add(css.editorBlockModalLabel);
    titleLabel.textContent = 'Title';
    this._titleInput = document.createElement('input');
    this._titleInput.classList.add(css.editorBlockModalInput);
    this._titleInput.type = 'text';
    this._titleInput.placeholder = 'e.g. Global Portfolio';
    this._titleInput.value = this._data.title;

    // Map style dropdown
    const styleLabel = document.createElement('label');
    styleLabel.classList.add(css.editorBlockModalLabel);
    styleLabel.textContent = 'Map Style';
    this._styleSelect = document.createElement('select');
    this._styleSelect.classList.add('dynamic-map-modal-select', css.editorBlockModalInput);

    const lightOption = document.createElement('option');
    lightOption.value = 'light';
    lightOption.textContent = 'Light';
    const darkOption = document.createElement('option');
    darkOption.value = 'dark';
    darkOption.textContent = 'Dark';

    this._styleSelect.appendChild(lightOption);
    this._styleSelect.appendChild(darkOption);
    this._styleSelect.value = this._data.mapStyle;

    // Region filter
    const regionLabel = document.createElement('label');
    regionLabel.classList.add(css.editorBlockModalLabel);
    regionLabel.textContent = 'Region';
    this._regionSelect = document.createElement('select');
    this._regionSelect.classList.add('dynamic-map-modal-select', css.editorBlockModalInput);
    this._regionSelect.innerHTML = '<option value="">All Regions</option>';
    this._regionSelect.addEventListener('change', () => this._onRegionChange());

    // Country filter (row hidden by default)
    this._countryRow = document.createElement('div');
    this._countryRow.style.display = 'none';
    const countryLabel = document.createElement('label');
    countryLabel.classList.add(css.editorBlockModalLabel);
    countryLabel.textContent = 'Country';
    this._countrySelect = document.createElement('select');
    this._countrySelect.classList.add('dynamic-map-modal-select', css.editorBlockModalInput);
    this._countrySelect.innerHTML = '<option value="">All Countries</option>';
    this._countrySelect.disabled = true;
    this._countrySelect.addEventListener('change', () => this._onCountryChange());
    this._countryRow.appendChild(countryLabel);
    this._countryRow.appendChild(this._countrySelect);

    // City filter (row hidden by default)
    this._cityRow = document.createElement('div');
    this._cityRow.style.display = 'none';
    const cityLabel = document.createElement('label');
    cityLabel.classList.add(css.editorBlockModalLabel);
    cityLabel.textContent = 'City';
    this._citySelect = document.createElement('select');
    this._citySelect.classList.add('dynamic-map-modal-select', css.editorBlockModalInput);
    this._citySelect.innerHTML = '<option value="">All Cities</option>';
    this._citySelect.disabled = true;
    this._citySelect.addEventListener('change', () => this._onCityChange());
    this._cityRow.appendChild(cityLabel);
    this._cityRow.appendChild(this._citySelect);

    // Reset button
    const resetBtnContainer = document.createElement("div");
    resetBtnContainer.classList.add(css.dmModalResetContainer);
    this._resetBtn = document.createElement('button');
    this._resetBtn.classList.add(css.dmModalResetBtn, css.blockBtn, css.blockBtnLarge, css.blockBtnLargeWide, css.clear);
    this._resetBtn.textContent = 'Reset';
    this._resetBtn.style.display = 'none';
    this._resetBtn.addEventListener('click', () => {
      this._onReset();
    });

    // Actions
    const actions = document.createElement('div');
    actions.classList.add('dynamic-map-modal-actions');

    const cancelBtn = document.createElement('button');
    cancelBtn.classList.add(css.dmModalCancelBtn, css.blockBtn, css.blockBtnLarge, css.blockBtnLargeWide, css.cancel);
    cancelBtn.textContent = 'Cancel';
    cancelBtn.addEventListener('click', () => this._hideModal());

    const applyBtn = document.createElement('button');
    applyBtn.classList.add(css.dmModalApplyBtn, css.blockBtn,  css.blockBtnLarge, css.blockBtnLargeWide, css.save);
    applyBtn.textContent = 'Apply';
    applyBtn.addEventListener('click', () => this._applySettings());

    resetBtnContainer.appendChild(this._resetBtn);
    actions.appendChild(resetBtnContainer);
    actions.appendChild(cancelBtn);
    actions.appendChild(applyBtn);

    inner.appendChild(heading);
    inner.appendChild(titleLabel);
    inner.appendChild(this._titleInput);
    inner.appendChild(styleLabel);
    inner.appendChild(this._styleSelect);
    inner.appendChild(regionLabel);
    inner.appendChild(this._regionSelect);
    inner.appendChild(this._countryRow);
    inner.appendChild(this._cityRow);
    inner.appendChild(this._buildOfflineFallbackSection());
    inner.appendChild(actions);

    modal.appendChild(backdrop);
    modal.appendChild(inner);

    return modal;
  }

  _buildOfflineFallbackSection() {
    const css = this._CSS;
    const wrapper = document.createElement('div');
    wrapper.classList.add('dm-offline-fallback');

    const label = document.createElement('label');
    label.classList.add(css.editorBlockModalLabel);
    label.textContent = 'Offline fallback image';

    this._offlineFallbackContainer = document.createElement('div');
    this._offlineFallbackContainer.classList.add(
      'modal-fallback-img-container',
      'editor-block-modal-img-container',
    );

    this._renderOfflineFallback();

    wrapper.appendChild(label);
    wrapper.appendChild(this._offlineFallbackContainer);
    return wrapper;
  }

  _renderOfflineFallback() {
    const css = this._CSS;
    if (!this._offlineFallbackContainer) return;

    this._offlineFallbackContainer.innerHTML = '';

    if (this._data.url) {
      const img = document.createElement('img');
      img.src = this._data.url;
      img.setAttribute('data-mid', this._data.mid);
      img.setAttribute('data-x', this._data.x);
      img.setAttribute('data-y', this._data.y);
      img.setAttribute('data-width', this._data.width);
      img.setAttribute('data-height', this._data.height);
      img.setAttribute('data-maxwidth', this._data.maxWidth);
      img.setAttribute('data-maxheight', this._data.maxHeight);
      img.classList.add('modal-fallback-img');
      img.style.width = '200px';
      img.style.height = '112.5px';
      img.style.objectFit = 'cover';
      this._offlineFallbackContainer.appendChild(img);
    }

    const btn = document.createElement('button');
    btn.classList.add(
      'map-fallback-btn',
      css.blockBtn,
      css.blockBtnSmall,
      css.xxlIcon,
    );
    btn.innerHTML = ImageIcon;
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      if (!this.config?.showMediaLibrary) return;
      const blockIndex = this.api.blocks.getCurrentBlockIndex();
      const block = this.api.blocks.getBlockByIndex(blockIndex);
      this.config.showMediaLibrary({
        details: block,
        data: this._data,
        bundle: 'image',
        slideFiles: {
          minWidth: 1366,
          minHeight: 768,
          maxWidth: 1920,
          maxHeight: 1080,
        },
      });
    });

    this._offlineFallbackContainer.appendChild(btn);
  }

  _showModal() {
    if (this._modal) {
      this._titleInput.value = this._data.title;
      this._styleSelect.value = this._data.mapStyle;
      this._updateResetButton();
      this._modal.classList.add('show');
    }
  }

  _hideModal() {
    if (this._modal) {
      this._modal.classList.remove('show');
    }
  }

  _applySettings() {
    this._data.title = this._titleInput.value.trim();
    this._data.mapStyle = this._styleSelect.value;

    this._hideModal();

    // Re-render iframe with new params
    this._renderIframe();

    if (this.block?.dispatchChange) {
      this.block.dispatchChange();
    }
  }

  save() {
    return {
      title: this._data.title,
      zoom: this._data.zoom,
      centerLat: this._data.centerLat,
      centerLng: this._data.centerLng,
      mapStyle: this._data.mapStyle,
      region: this._data.region,
      country: this._data.country,
      city: this._data.city,
      url: this._data.url || '',
      mid: this._data.mid || '',
      x: this._data.x || '',
      y: this._data.y || '',
      width: this._data.width || '',
      height: this._data.height || '',
      maxWidth: this._data.maxWidth || '',
      maxHeight: this._data.maxHeight || '',
    };
  }

  validate() {
    return true;
  }

  destroyed() {
    window.removeEventListener('message', this._onMessage);
  }
}
